import React, { useState } from 'react';
import { Badge, Box, ButtonBase, IconButton, InputBase, Skeleton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import { SupportCenterHeaderBadge } from '../SupportCenterIcon';
import type { AssistantConversationSummary, HelpArticleSummary, HelpNewsItem } from '../../../services/assistantCentral';
import { conversationListTitle } from '../assistant/assistantConversationFormat';
import { NewsCover, NewsMeta } from './MessengerNews';
import {
  MESSENGER,
  formatMessengerRelativeTime,
  messengerCardButtonSx,
  messengerCardSx,
  messengerCardVerticalButtonSx,
  messengerSearchShellSx,
  messengerSubtitleSx,
  messengerTitleSx,
} from './messengerTheme';

interface MessengerHomeProps {
  brandName: string;
  firstName?: string;
  recentConversation: AssistantConversationSummary | null;
  recentUnread: boolean;
  suggested: HelpArticleSummary[] | null;
  latestNews: HelpNewsItem | null;
  onClose: () => void;
  onOpenConversation: (id: string) => void;
  onNewMessage: () => void;
  onSearch: (query: string) => void;
  onOpenArticle: (slug: string) => void;
  onOpenNews: (item: HelpNewsItem) => void;
}

const MessengerHome: React.FC<MessengerHomeProps> = ({
  brandName,
  firstName,
  recentConversation,
  recentUnread,
  suggested,
  latestNews,
  onClose,
  onOpenConversation,
  onNewMessage,
  onSearch,
  onOpenArticle,
  onOpenNews,
}) => {
  const [query, setQuery] = useState('');

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: MESSENGER.canvas }}>
      <Box sx={{ background: MESSENGER.heroGradient, px: 2.5, pt: 2.5, pb: 9 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <SupportCenterHeaderBadge size={34} />
          <Typography sx={{ flex: 1, fontWeight: 800, fontSize: '1.15rem', color: '#fff', letterSpacing: '-0.02em' }} noWrap>
            {brandName}
          </Typography>
          <Tooltip title="Fechar">
            <IconButton size="small" onClick={onClose} aria-label="Fechar" sx={{ color: '#fff' }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ mt: 4.5 }}>
          <Typography
            component="h2"
            sx={{ fontWeight: 800, fontSize: '1.6rem', lineHeight: 1.2, color: '#fff', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {firstName ? `Olá, ${firstName}` : 'Olá'}
            <WavingHandIcon sx={{ fontSize: 26, color: '#FCD34D' }} aria-hidden />
          </Typography>
          <Typography component="p" sx={{ fontWeight: 800, fontSize: '1.6rem', lineHeight: 1.2, color: '#fff', letterSpacing: '-0.02em' }}>
            Como podemos ajudar?
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2, mt: -7, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {recentConversation && (
          <ButtonBase onClick={() => onOpenConversation(recentConversation.id)} sx={{ ...messengerCardButtonSx, display: 'block', py: 1.75 }}>
            <Typography sx={{ ...messengerTitleSx, mb: 1.25 }}>Mensagem recente</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Badge color="error" variant="dot" invisible={!recentUnread} overlap="circular">
                <SupportCenterHeaderBadge size={36} />
              </Badge>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography noWrap sx={{ flex: 1, fontSize: '0.84rem', color: MESSENGER.navy, fontWeight: recentUnread ? 700 : 500 }}>
                    {conversationListTitle(recentConversation)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: MESSENGER.slateFaint, flexShrink: 0 }}>
                    {formatMessengerRelativeTime(recentConversation.last_message_at)}
                  </Typography>
                </Box>
                <Typography noWrap sx={messengerSubtitleSx}>
                  {recentConversation.last_message_preview || 'Toque para continuar a conversa'}
                </Typography>
              </Box>
            </Box>
          </ButtonBase>
        )}

        <ButtonBase onClick={onNewMessage} sx={messengerCardButtonSx}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={messengerTitleSx}>Envie uma mensagem</Typography>
            <Typography sx={messengerSubtitleSx}>A IA responde na hora · equipe Klinvia quando precisar</Typography>
          </Box>
          <SendRoundedIcon sx={{ color: MESSENGER.teal, fontSize: 22 }} />
        </ButtonBase>

        <Box sx={{ ...messengerCardSx, p: 1 }}>
          <Box
            component="form"
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              onSearch(query.trim());
            }}
            sx={messengerSearchShellSx}
          >
            <InputBase
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value.slice(0, 200))}
              placeholder="Qual é a sua dúvida?"
              inputProps={{ 'aria-label': 'Pesquisar na central de ajuda' }}
              sx={{ fontSize: '0.875rem', fontWeight: 600, py: 0.75, color: MESSENGER.navy, '& input::placeholder': { color: MESSENGER.navy, opacity: 0.85 } }}
            />
            <IconButton type="submit" size="small" aria-label="Pesquisar" sx={{ color: MESSENGER.navy }}>
              <SearchIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
          <Box sx={{ mt: 0.5 }}>
            {suggested === null
              ? [0, 1, 2].map((i) => <Skeleton key={i} height={44} sx={{ mx: 1 }} />)
              : suggested.map((a) => (
                  <ButtonBase
                    key={a.slug}
                    onClick={() => onOpenArticle(a.slug)}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      textAlign: 'left',
                      px: 1.25,
                      py: 1.1,
                      borderRadius: '8px',
                      '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.04)' },
                    }}
                  >
                    <Typography sx={{ flex: 1, fontSize: '0.84rem', color: MESSENGER.slate, lineHeight: 1.4 }}>{a.title}</Typography>
                    <ChevronRightIcon sx={{ fontSize: 20, color: MESSENGER.navy }} />
                  </ButtonBase>
                ))}
          </Box>
        </Box>

        {latestNews && (
          <ButtonBase onClick={() => onOpenNews(latestNews)} sx={messengerCardVerticalButtonSx}>
            <NewsCover item={latestNews} height={130} />
            <Box sx={{ px: 2, py: 1.6 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: MESSENGER.tealDark, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
                Novo no blog
              </Typography>
              <NewsMeta item={latestNews} />
              <Typography sx={{ ...messengerTitleSx, mt: 0.75 }}>{latestNews.title}</Typography>
              <Typography
                sx={{ ...messengerSubtitleSx, mt: 0.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {latestNews.summary}
              </Typography>
            </Box>
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
};

export default MessengerHome;
